import { FC, useState } from 'react';
import { useRouter } from 'next/router';
import clsx from 'clsx';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';

import Button from 'epn-ui/umd/dist/components/Button';
import useI18n from '@i18n';
import BaseSemanticLink from '@base/BaseSemanticLink';
import config, { ISidebarConfigItem, SidebarConfigAppearsTypes } from './config';
import styles from './styles.module.scss';

interface INestedItemValue {
    id: string;
    key: string;
    link: string;
}

interface INestedItemsProps {
    currentNested: {
        key: string;
        values: INestedItemValue[];
        id: string;
    };
    setIsNested: (isNested: boolean) => void;
}

const NestedItems: FC<INestedItemsProps> = ({
    currentNested: { key, values, id },
    setIsNested,
}) => {
    const { t, locale } = useI18n('Tools');
    return (
        <div className="flex flex-wrap items-center">
            <Button
                id={id}
                type="text"
                className={`${styles.sharedStyles} mb-0`}
                onClick={() => setIsNested(false)}
                size="large"
            >
                <FontAwesomeIcon icon={faTimes} className="text-16px text-blueGray-500" />
                <span>{t(key)}</span>
            </Button>
            <FontAwesomeIcon icon={faChevronRight} className="text-blueGray-500" />
            {values.map(item => {
                return (
                    <Button
                        id={item.id}
                        key={item.key}
                        className="hover:text-red-500 text-blueGray-500"
                        href={`/${locale}${item.link}`}
                        size="large"
                        type="text"
                    >
                        {t(item.key)}
                    </Button>
                );
            })}
        </div>
    );
};

interface IItemProps extends ISidebarConfigItem {
    onClick: () => void;
}

const Item: React.FC<IItemProps> = ({
    link = null,
    onClick = null,
    newTool = false,
    hidden = false,
    appears = '',
    children,
    id = '',
    newTab = false,
}) => {
    const { locale } = useI18n();
    const { asPath } = useRouter();

    const component = link ? 'link' : 'button';

    const appearClassMap = {
        [SidebarConfigAppearsTypes.xs]: 'xs:inline-flex',
        [SidebarConfigAppearsTypes.sm]: 'sm:inline-flex',
        default: 'lg:inline-flex',
    };

    const appearClass = appearClassMap[appears || 'default'];

    const classes = clsx(
        {
            hidden,
        },
        appearClass,
    );

    const active = asPath === `/${locale}${link}` ? 'bg-blueGray-200 text-blueGray-800' : '';

    const cmpConfig = {
        button: {
            component: Button,
            props: {
                id,
                onClick,
                className: `${styles.sharedStyles} rounded-3 p-[8px] text-[16px] ${
                    hidden ? 'hidden' : ''
                }`,
                size: 'large',
                type: 'transparent',
            },
        },
        link: {
            component: BaseSemanticLink,
            props: {
                id,
                ...(newTab && {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                }),
                to: link,
                className: `${
                    hidden ? 'hidden' : ''
                } inline-flex items-center bg-blueGray-100 text-brandBlue-300/90 rounded-3 p-[8px] cursor-pointer h-[40px] text-slate-500 mr-[16px] mb-[16px] hover:text-brandBlue-500 hover:bg-slate-100 ${active}`,
            },
        },
    };

    const Component: FC<unknown> = cmpConfig[component].component;
    const { props } = cmpConfig[component];

    return (
        <Component
            className={`${classes} ${active} flex items-center rounded-3 p-2 h-10 text-blueGray-500 bg-blueGray-100`}
            {...props}
        >
            <span>{children}</span>
            {newTool && (
                <span className="items-center py-0 text-white rounded bg-brandColor leading-[16px] text-[10px] px-[4px] ml-[4px]">
                    New
                </span>
            )}
        </Component>
    );
};

const MobileTools: React.FC = () => {
    const { t } = useI18n('Tools');

    const [isNested, setIsNested] = useState(false);
    const [currentNested, setCurrentNested] = useState(null);
    const [isHidden, setIsHidden] = useState(true);

    return (
        <>
            <h3 className="font-medium text-[28px] leading-h1 mb-[24px] text-blueGray-800">
                {t('Tools')}
            </h3>
            <div className="flex flex-wrap items-center ">
                {!isNested &&
                    config.map(item => {
                        const { key, link, newTool, hidden, appears, id, newTab } = item;

                        return (
                            <Item
                                key={key}
                                id={id}
                                newTab={newTab}
                                link={link}
                                onClick={() => {
                                    setIsNested(!isNested);
                                    setCurrentNested(item);
                                }}
                                newTool={newTool}
                                hidden={isHidden && hidden}
                                appears={appears}
                            >
                                {t(key)}
                            </Item>
                        );
                    })}

                {!isNested && isHidden && (
                    <button
                        id="SidebarMobileMoreButton"
                        type="button"
                        className={`${styles.sharedStyles} bg-transparent hover:bg-brandBlue-300 hover:text-white`}
                        onClick={() => {
                            setIsHidden(false);
                        }}
                    >
                        <span className="mr-[8px]">{t('More')}</span>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                )}

                {!isNested && !isHidden && (
                    <button
                        id="SidebarMobileHideButton"
                        type="button"
                        className={`${styles.sharedStyles} bg-transparent hover:bg-brandBlue-300 hover:text-white`}
                        onClick={() => {
                            setIsHidden(true);
                        }}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                        <span className="ml-[8px]">{t('Hide')}</span>
                    </button>
                )}

                {isNested && (
                    <NestedItems currentNested={currentNested} setIsNested={setIsNested} />
                )}
            </div>
        </>
    );
};

export default MobileTools;
